import { MailboxProperty, ColumnConfig } from '@/hooks/useMailboxProperties'

/**
 * Default column visibility and display preferences
 * This determines which columns are visible by default in the table
 */
const DEFAULT_COLUMN_PREFERENCES = {
  // Merged name/email column (replaces email, from_name, domain)
  name_email: { visible: true, order: 0, pinned: false, default: true },

  // Old core columns - hidden by default (merged into name_email)
  email: { visible: false, order: 50, pinned: false, default: false },
  from_name: { visible: false, order: 51, pinned: false, default: false },
  domain: { visible: false, order: 52, pinned: false, default: false },

  // Status and connection
  status: { visible: true, order: 1, pinned: false, default: true },
  smartlead_connected: { visible: true, order: 2, pinned: false, default: true },
  masterinbox_connected: { visible: true, order: 2.5, pinned: false, default: true },

  // Merged warmup column (replaces warmup_status + warmup_reputation)
  warmup: { visible: true, order: 3, pinned: false, default: true },
  warmup_status: { visible: false, order: 71, pinned: false, default: false },
  warmup_reputation: { visible: false, order: 72, pinned: false, default: false },

  // Providers from domains table (replaces old provider)
  mailbox_provider: { visible: true, order: 4, pinned: false, default: true },
  email_provider: { visible: true, order: 5, pinned: false, default: true },
  provider: { visible: false, order: 53, pinned: false, default: false },
  client_id: { visible: false, order: 54, pinned: false, default: false },

  // Old sending metrics - hidden by default
  daily_sent_count: { visible: false, order: 55, pinned: false, default: false },
  message_per_day: { visible: false, order: 56, pinned: false, default: false },
  current_daily_sent: { visible: false, order: 57, pinned: false, default: false },
  daily_limit: { visible: false, order: 58, pinned: false, default: false },

  // Warmup metrics
  warmup_sent_total: { visible: false, order: 59, pinned: false, default: false },

  // Campaign Metrics (primary sending metrics)
  total_emails_sent: { visible: true, order: 6, pinned: false, default: true },
  total_email_replies: { visible: true, order: 7, pinned: false, default: true },
  total_email_bounces: { visible: true, order: 8, pinned: false, default: true },

  // Tags column
  tags: { visible: true, order: 9, pinned: false, default: true },

  // Signature - positioned just before Notes
  signature: { visible: true, order: 98, pinned: false, default: true },

  // Smartlead Sync Fields - hidden by default
  bounce_rate: { visible: false, order: 59, pinned: false, default: false },
  spam_score: { visible: false, order: 60, pinned: false, default: false },
  connection_health: { visible: false, order: 61, pinned: false, default: false },
  sequencer_platform: { visible: false, order: 62, pinned: false, default: false },
  sequencer_mailbox_id: { visible: false, order: 63, pinned: false, default: false },
  last_connection_check: { visible: false, order: 64, pinned: false, default: false },
  connection_error: { visible: false, order: 65, pinned: false, default: false },

  // Sync status
  last_api_synced_at: { visible: false, order: 66, pinned: false, default: false },
  api_sync_status: { visible: false, order: 67, pinned: false, default: false },

  // Calculated fields
  days_warming_up: { visible: false, order: 68, pinned: false, default: false },
  utilization_percentage: { visible: false, order: 69, pinned: false, default: false },
  spam_rate: { visible: false, order: 70, pinned: false, default: false },

  // Notes column - positioned as last before Actions
  notes: { visible: true, order: 99, pinned: false, default: true },
}

/**
 * Maps mailbox properties to column configurations for table display
 */
export function mapPropertiesToColumns(properties: MailboxProperty[]): ColumnConfig[] {
  return properties.map((property, index) => {
    const preference = DEFAULT_COLUMN_PREFERENCES[property.id as keyof typeof DEFAULT_COLUMN_PREFERENCES]
    
    return {
      id: property.id,
      label: property.label,
      visible: preference?.visible ?? false,
      order: preference?.order ?? (100 + index), // Default order for unmapped properties
      pinned: preference?.pinned ?? false,
      default: preference?.default ?? false
    }
  })
}

/**
 * Merges user preferences with property definitions
 * User preferences override defaults but missing properties are added from property definitions
 */
export function mergeUserPreferencesWithProperties(
  properties: MailboxProperty[],
  userColumns: ColumnConfig[],
  userLabels: Record<string, string> = {}
): ColumnConfig[] {
  const propertyMap = new Map(properties.map(prop => [prop.id, prop]))
  const userColumnMap = new Map(userColumns.map(col => [col.id, col]))
  
  // Start with all properties from the definition
  const mergedColumns: ColumnConfig[] = properties.map((property, index) => {
    const userColumn = userColumnMap.get(property.id)
    const defaultPreference = DEFAULT_COLUMN_PREFERENCES[property.id as keyof typeof DEFAULT_COLUMN_PREFERENCES]
    
    return {
      id: property.id,
      label: userLabels[property.id] || userColumn?.label || property.label,
      visible: userColumn?.visible ?? defaultPreference?.visible ?? false,
      order: userColumn?.order ?? defaultPreference?.order ?? (100 + index),
      pinned: userColumn?.pinned ?? defaultPreference?.pinned ?? false,
      default: defaultPreference?.default ?? false
    }
  })
  
  // Sort by order
  return mergedColumns.sort((a, b) => (a.order || 0) - (b.order || 0))
}

/**
 * Validates that a column configuration is compatible with property definitions
 */
export function validateColumnConfig(
  columns: ColumnConfig[],
  properties: MailboxProperty[]
): { valid: boolean; missingProperties: string[]; extraColumns: string[] } {
  const propertyIds = new Set(properties.map(prop => prop.id))
  const columnIds = new Set(columns.map(col => col.id))
  
  const missingProperties = properties
    .filter(prop => !columnIds.has(prop.id))
    .map(prop => prop.id)
  
  const extraColumns = columns
    .filter(col => !propertyIds.has(col.id))
    .map(col => col.id)
  
  return {
    valid: missingProperties.length === 0 && extraColumns.length === 0,
    missingProperties,
    extraColumns
  }
}

/**
 * Gets the storage key for column configurations
 */
export function getColumnConfigStorageKey(tableType: string = 'mailbox'): string {
  return `${tableType}-column-config`
}

/**
 * Gets the storage key for custom column labels
 */
export function getColumnLabelsStorageKey(tableType: string = 'mailbox'): string {
  return `${tableType}-column-labels`
}

/**
 * Loads column configuration from localStorage with property validation
 */
export function loadColumnConfigFromStorage(
  properties: MailboxProperty[],
  tableType: string = 'mailbox'
): { columns: ColumnConfig[]; labels: Record<string, string> } {
  try {
    const configKey = getColumnConfigStorageKey(tableType)
    const labelsKey = getColumnLabelsStorageKey(tableType)
    
    const savedConfig = localStorage.getItem(configKey)
    const savedLabels = localStorage.getItem(labelsKey)
    
    let userColumns: ColumnConfig[] = []
    let userLabels: Record<string, string> = {}
    
    if (savedConfig) {
      userColumns = JSON.parse(savedConfig)
    }
    
    if (savedLabels) {
      userLabels = JSON.parse(savedLabels)
    }
    
    // Merge user preferences with current property definitions
    const columns = mergeUserPreferencesWithProperties(properties, userColumns, userLabels)
    
    return { columns, labels: userLabels }
  } catch (error) {
    console.error('Failed to load column config from storage:', error)
    // Return default configuration
    return {
      columns: mapPropertiesToColumns(properties),
      labels: {}
    }
  }
}

/**
 * Saves column configuration to localStorage
 */
export function saveColumnConfigToStorage(
  columns: ColumnConfig[],
  labels: Record<string, string>,
  tableType: string = 'mailbox'
): void {
  try {
    const configKey = getColumnConfigStorageKey(tableType)
    const labelsKey = getColumnLabelsStorageKey(tableType)
    
    localStorage.setItem(configKey, JSON.stringify(columns))
    localStorage.setItem(labelsKey, JSON.stringify(labels))
  } catch (error) {
    console.error('Failed to save column config to storage:', error)
  }
}